/**
 * Memory Module Hooks
 *
 * React Query hooks for memory management:
 * - useMemoryStats: Get memory usage statistics
 * - useMemoryConfig: Get effective memory configuration
 * - useMemoryPresets: List available presets
 * - useMemoryNotifications: Get memory notifications
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

// =============================================================================
// TYPES
// =============================================================================

export interface MemoryStats {
  episode_count: number
  semantic_node_count: number
  procedural_pattern_count: number
  semantic_size_bytes: number
  oldest_episode: string | null
  newest_episode: string | null
}

export interface MemoryNotification {
  id: string
  type: string
  title: string
  message: string
  priority: string
  data: Record<string, unknown>
  created_at: string
  read: boolean
  dismissed: boolean
}

// =============================================================================
// HOOKS
// =============================================================================

export function useMemoryStats() {
  return useQuery<MemoryStats>({
    queryKey: ['memory', 'stats'],
    queryFn: async () => {
      const { data } = await api.get('/api/memory/stats')
      return data
    },
  })
}

export function useMemoryConfig() {
  return useQuery<Record<string, unknown>>({
    queryKey: ['memory', 'config'],
    queryFn: async () => {
      const { data } = await api.get('/api/memory/config')
      return data
    },
  })
}

export function useMemoryPresets(category?: string) {
  return useQuery<Record<string, unknown>>({
    queryKey: ['memory', 'presets', category],
    queryFn: async () => {
      const params = category ? { category } : {}
      const { data } = await api.get('/api/memory/config/presets', { params })
      return data
    },
  })
}

export function useMemoryNotifications(unreadOnly = true) {
  return useQuery<{ notifications: MemoryNotification[]; unread_count: number }>({
    queryKey: ['memory', 'notifications', unreadOnly],
    queryFn: async () => {
      const { data } = await api.get('/api/memory/notifications', {
        params: { unread_only: unreadOnly },
      })
      return data
    },
  })
}

export function useMemoryExport() {
  return useMutation({
    mutationFn: async (params: {
      include_episodes?: boolean
      include_semantic?: boolean
      include_procedural?: boolean
    }) => {
      const { data } = await api.get('/api/memory/sovereignty/export', { params })
      return data
    },
  })
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { data } = await api.post(`/api/memory/notifications/${notificationId}/read`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memory', 'notifications'] })
    },
  })
}
